import { ApplicationType } from '@thesavingsgroup/enums/ApplicationType';
import { VehicleCondition } from '@thesavingsgroup/enums/VehicleCondition';
import { isNonEmptyString } from '@thesavingsgroup/utils/isNonEmptyString';
import i18n from 'i18next';
import {
  find,
  get as lodashGet,
  gt,
  head,
  isEmpty,
  isEqual,
  isObject,
  toString,
} from 'lodash';
import { StateCreator } from 'zustand';

import { isInEnum } from '../../utils/isInEnum';
import { DashboardVehicle } from '../dashboardStore/types/DashboardVehicle';
import { HydrateSource } from './enums/HydrateSource';
import { PurchaseType } from './enums/PurchaseType';
import { RefinanceGoal } from './enums/RefinanceGoal';
import { SearchVehicle } from './enums/SearchVehicle';
import { VehicleImageType } from './enums/VehicleImageType';
import { toApplyStateFromCanceledApplication } from './functions/toApplyStateFromCanceledApplication';
import { toApplyStateFromMailCode } from './functions/toApplyStateFromMailCode';
import { toApplyStateFromVehicleSelection } from './functions/toApplyStateFromVehicleSelection';
import { ApplyResponse } from './types/ApplyResponse';
import { ApplyState } from './types/ApplyState';
import { ApplyStateDependencies } from './types/ApplyStateDependencies';
import { FetchVehicleIdentifiersResponse } from './types/FetchVehicleIdentifiersResponse';
import { HydrateParams } from './types/HydrateParams';
import { Vehicle } from './types/Vehicle';
import { VehicleCriteriaValue } from './types/VehicleCriteriaValue';
import { VehicleImage } from './types/VehicleImage';
import { VehicleMake } from './types/VehicleMake';
import { VehicleModel } from './types/VehicleModel';
import { VehicleTrim } from './types/VehicleTrim';

export const keysToPersist = [
  'applicationType',
  'isHydrated',
  'make',
  'mileage',
  'model',
  'payoffAmount',
  'plate',
  'purchasePrice',
  'purchaseType',
  'refinanceGoal',
  'searchVehicle',
  'state',
  'trim',
  'vehicleCondition',
  'vehicleSelectionId',
  'vin',
  'year',
];

export const applyStore =
  ({
    useAnalyticStore,
    useApiStore,
    useAttributionStore,
    useDashboardStore,
    useLeadChannelStore,
    useMailCodeStore,
    useParameterStore,
    useWorkflowStore,
  }: ApplyStateDependencies): StateCreator<ApplyState> =>
  (set, get) => ({
    applicationType: null,
    fetchVehicleFailedMessage: null,
    fetchVehicleImageByTrimFailed: false,
    fetchVehicleMakesFailedMessage: null,
    fetchVehicleModelsFailedMessage: null,
    fetchVehicleSelectionsCompleted: false,
    fetchVehicleSuccess: false,
    fetchVehicleTrimsFailedMessage: null,
    isDuplicateVehicleError: false,
    isFetchingVehicleMakes: false,
    isFetchingVehicleModels: false,
    isFetchingVehicleSelections: false,
    isHydrated: false,
    make: null,
    makes: [],
    mileage: null,
    model: null,
    models: [],
    payoffAmount: null,
    plate: null,
    purchasePrice: null,
    purchaseType: null,
    refinanceGoal: null,
    searchVehicle: null,
    state: null,
    submitCompleted: false,
    submitFailedMessage: null,
    trim: null,
    trims: [],
    uniqueIdentifier: undefined,
    vehicleCondition: null,
    vehicleImage: null,
    vehicleSelectionId: null,
    vehicleSelections: [],
    vin: null,
    year: null,

    computed: {
      get canResumeWorkflow(): boolean {
        const { isHydrated } = get();
        const {
          computed: { hasCompletedPages },
        } = useWorkflowStore.getState();

        return !isHydrated && hasCompletedPages;
      },
      get hasValidApplicationType(): boolean {
        return isInEnum(get().applicationType, ApplicationType);
      },
      get hasValidPayoffAmount(): boolean {
        return gt(Number(get().payoffAmount), 0);
      },
      get hasValidPurchasePrice(): boolean {
        return gt(Number(get().purchasePrice), 0);
      },
      get hasValidPurchaseType(): boolean {
        return isInEnum(get().purchaseType, PurchaseType);
      },
      get hasValidRefinanceGoal(): boolean {
        return isInEnum(get().refinanceGoal, RefinanceGoal);
      },
      get hasValidVehicleCondition(): boolean {
        return isInEnum(get().vehicleCondition, VehicleCondition);
      },
      get hasValidVehicleLicensePlate(): boolean {
        const { plate, state } = get();

        return [plate, state].every(isNonEmptyString);
      },
      get hasValidVehicleMake(): boolean {
        const { make } = get();

        return (
          isObject(make) &&
          isNonEmptyString(make.label) &&
          isNonEmptyString(make.value)
        );
      },
      get hasValidVehicleMileage(): boolean {
        return gt(Number(get().mileage), 0);
      },
      get hasValidVehicleModel(): boolean {
        const { model } = get();

        return (
          isObject(model) &&
          isNonEmptyString(model.label) &&
          isNonEmptyString(model.value)
        );
      },
      get hasValidVehicleSearch(): boolean {
        return isInEnum(get().searchVehicle, SearchVehicle);
      },
      get hasValidVehicleTrim(): boolean {
        const { trim } = get();

        return (
          isObject(trim) &&
          isNonEmptyString(trim.label) &&
          isNonEmptyString(trim.value)
        );
      },
      get hasValidVehicleVin(): boolean {
        const { vin } = get();

        return isNonEmptyString(vin);
      },
      get hasValidVehicleYear(): boolean {
        const { year } = get();

        return gt(Number(year), 0);
      },
      get hasVehiclesForSelection(): boolean {
        const {
          computed: { isVehicleSelectionsEmpty },
          fetchVehicleSelectionsCompleted,
        } = get();

        // If we haven't fetched yet, default to true
        if (!fetchVehicleSelectionsCompleted) {
          return true;
        }

        // Once fetched, return whether we actually have vehicles
        return !isVehicleSelectionsEmpty;
      },
      get isVehicleSelectionIdEmpty(): boolean {
        const { vehicleSelectionId } = get();

        return isEmpty(vehicleSelectionId);
      },
      get isVehicleSelectionsEmpty(): boolean {
        const { vehicleSelections } = get();

        return isEmpty(vehicleSelections);
      },
      get searchVehicleSelectionsPayload(): Record<string, unknown> {
        const {
          computed: { partnerRecordUniqueIdentifier },
        } = useAttributionStore.getState();
        const {
          computed: { defaultLeadChannelCode },
        } = useLeadChannelStore.getState();
        const { leadChannelCode } = useParameterStore.getState();
        const { findMailCodeDataByUniqueIdentifier } =
          useMailCodeStore.getState();

        const mailCodeData = findMailCodeDataByUniqueIdentifier(
          partnerRecordUniqueIdentifier,
        );

        return {
          leadChannelCode: leadChannelCode || defaultLeadChannelCode,

          ...(mailCodeData && {
            physicalAddress: {
              city: mailCodeData.payload.applicant.city,
              lineOne: mailCodeData.payload.applicant.address,
              postalCode: mailCodeData.payload.applicant.zipCode,
              province: mailCodeData.payload.applicant.state,
            },
          }),
        };
      },
      get submitPayload(): Record<string, unknown> {
        const {
          applicationType,
          make,
          mileage,
          model,
          payoffAmount,
          plate,
          purchasePrice,
          purchaseType,
          refinanceGoal,
          state,
          trim,
          vehicleCondition,
          vin,
          year,
        } = get();

        const {
          computed: { partnerRecordUniqueIdentifier },
        } = useAttributionStore.getState();
        const { leadChannelCode } = useParameterStore.getState();

        return {
          // important to prefer `purchaseType` over `applicationType` as the
          // `applicationType` always has a value (REFINANCE or PURCHASE),
          // but the purchase type is a more specific value that we want to
          // use if it's available (DEALER, LEASE_BUYOUT, PRIVATE_PARTY, etc)
          applicationType: purchaseType || applicationType,
          leadChannelCode,
          make: make?.label,
          mileage: Number(mileage),
          model: model?.label,
          payoffAmount: payoffAmount ? Number(payoffAmount) : null,
          plate,
          province: state,
          purchasePrice: purchasePrice ? Number(purchasePrice) : null,
          refinanceGoal,
          trim: trim?.label,
          trimId: trim?.value,
          uniqueIdentifier: partnerRecordUniqueIdentifier,
          vehicleCondition,
          vin,
          year: Number(year),
        };
      },
      get yearMakeModel(): string {
        const { year, make, model } = get();

        const makeLabel = lodashGet(make, 'label');
        const modelLabel = lodashGet(model, 'label');

        return `${year} ${makeLabel} ${modelLabel}`;
      },
    },

    clear: (): void => {
      set({
        fetchVehicleFailedMessage: null,
        fetchVehicleImageByTrimFailed: false,
        fetchVehicleMakesFailedMessage: null,
        fetchVehicleModelsFailedMessage: null,
        fetchVehicleSuccess: false,
        fetchVehicleTrimsFailedMessage: null,
        makes: [],
        models: [],
        trims: [],
        vehicleImage: null,
      });
    },
    fetchVehicleBy: (
      vehicleCriteriaValue: VehicleCriteriaValue,
    ): Promise<void> => {
      const { searchVehicle } = get();

      const { plate = null, state = null, vin = null } = vehicleCriteriaValue;

      set({
        fetchVehicleFailedMessage: null,
        fetchVehicleSuccess: false,
        makes: [],
        models: [],
        trims: [],
        vehicleImage: null,
      });

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest
        .post<never, Vehicle[]>('/vehicles/search', {
          criteria: searchVehicle,
          value: vehicleCriteriaValue,
        })
        .then((vehicles) => {
          const vehicle = head(vehicles);

          const make: VehicleMake = {
            label: lodashGet(vehicle, 'make'),
            value: lodashGet(vehicle, 'makeId'),
          };

          const model: VehicleModel = {
            label: lodashGet(vehicle, 'model'),
            value: lodashGet(vehicle, 'modelId'),
          };

          const trim: VehicleTrim = {
            label: lodashGet(vehicle, 'trim'),
            value: lodashGet(vehicle, 'trimId'),
          };

          const year = lodashGet(vehicle, 'year');

          set({
            fetchVehicleSuccess: true,
            make,
            model,
            plate,
            state,
            trim,
            vin,
            year,
          });
        })
        .catch((error: unknown) => {
          const errorStatus = toString(lodashGet(error, 'response.status'));
          const defaultMessage = i18n.t('errors.fetchVehicleBy.defaultMessage');

          set({
            fetchVehicleFailedMessage: lodashGet(
              i18n.t('errors.fetchVehicleBy.statusMessage'),
              errorStatus,
              defaultMessage,
            ),
            make: null,
            model: null,
            plate: null,
            state: null,
            trim: null,
            vin: null,
            year: null,
          });
        });
    },
    fetchVehicleIdentifiers: async (
      vehicle: DashboardVehicle,
    ): Promise<FetchVehicleIdentifiersResponse> => {
      // There are multiple reasons as to why we need to always fetch the makes, models & trims.
      // It's because we require their respective ids (value) when hydrating the store:
      // - In cases where the vehicle has a vin or license plate:
      //   - we need to be able to call fetchVehicleTrim to populate the trim dropdown on the confirm vehicle page
      // - In cases where the vehicle only have make/model/trim labels:
      //   - we need to be able to call fetchVehicleTrim to populate the trim dropdown on the confirm vehicle page
      //   - we need to be able to pre-select the correct make/model options when going backwards from the confirm vehicle page
      // - In all cases, we need to have the trim id at time of submission (submitPayload)
      const { fetchVehicleMakes, fetchVehicleModels, fetchVehicleTrims } =
        get();

      set({ year: String(vehicle.year) });

      await fetchVehicleMakes();
      const make = find(get().makes, (m) => m.label === vehicle.make);

      set({ make });

      await fetchVehicleModels();
      const model = find(get().models, (m) => m.label === vehicle.model);

      set({ model });

      await fetchVehicleTrims();
      const trim = find(get().trims, (t) => t.label === vehicle.trim);

      return { make, model, trim, year: String(vehicle.year) };
    },
    fetchVehicleImageByTrim: (trim: VehicleTrim | null): Promise<void> => {
      const { year, make, model } = get();

      const makeValue = lodashGet(make, 'value');
      const modelValue = lodashGet(model, 'value');
      const trimValue = lodashGet(trim, 'value');
      if (!year || !makeValue || !modelValue || !trimValue) {
        return;
      }

      const portalHttpRequest = useApiStore.getState().portalApi();

      set({ fetchVehicleImageByTrimFailed: false, vehicleImage: null });

      return portalHttpRequest
        .get<never, VehicleImage[]>(
          `vehicles/years/${year}/makes/${makeValue}/models/${modelValue}/trims/${trimValue}/images`,
        )
        .then((images) => {
          const vehicleImage = images.find(
            (image) => image.vehicleImageType === VehicleImageType.DEFAULT,
          );

          const vehicleImageURL = lodashGet(vehicleImage, 'url', null);

          set({
            fetchVehicleImageByTrimFailed: isEmpty(vehicleImageURL),
            vehicleImage: vehicleImageURL,
          });
        })
        .catch(() => {
          set({ fetchVehicleImageByTrimFailed: true });
        });
    },
    fetchVehicleMakes: (): Promise<void> => {
      const { year } = get();

      if (!year) {
        return;
      }

      set({
        fetchVehicleMakesFailedMessage: null,
        isFetchingVehicleMakes: true,
        makes: [],
        models: [],
        trims: [],
      });

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest
        .get<never, VehicleMake[]>(`/vehicles/years/${year}/makes`)
        .then((makes) => {
          set({ makes });
        })
        .catch(() => {
          set({
            fetchVehicleMakesFailedMessage: i18n.t(
              'errors.fetchVehicleMakes.defaultMessage',
            ),
          });
        })
        .finally(() => {
          set({ isFetchingVehicleMakes: false });
        });
    },
    fetchVehicleModels: (): Promise<void> => {
      const { year, make } = get();

      const makeValue = lodashGet(make, 'value');
      if (!year || !makeValue) {
        return;
      }

      set({
        fetchVehicleModelsFailedMessage: null,
        isFetchingVehicleModels: true,
        models: [],
        trims: [],
      });

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest
        .get<never, VehicleModel[]>(
          `/vehicles/years/${year}/makes/${makeValue}/models`,
        )
        .then((models) => {
          set({ models });
        })
        .catch(() => {
          set({
            fetchVehicleModelsFailedMessage: i18n.t(
              'errors.fetchVehicleModels.defaultMessage',
            ),
          });
        })
        .finally(() => {
          set({ isFetchingVehicleModels: false });
        });
    },
    fetchVehicleSelections: (): Promise<void> => {
      const {
        computed: { searchVehicleSelectionsPayload },
        isFetchingVehicleSelections,
        fetchVehicleSelectionsCompleted,
      } = get();

      if (isFetchingVehicleSelections || fetchVehicleSelectionsCompleted) {
        return;
      }

      set({ isFetchingVehicleSelections: true });

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest
        .post<never, { vehicles: DashboardVehicle[] }>(
          '/2.0/apply/search',
          searchVehicleSelectionsPayload,
        )
        .then(({ vehicles }) => {
          set({ vehicleSelections: vehicles });
        })
        .catch(() => {
          // default to an empty array on error to allow the user to proceed with a manual entry
          set({ vehicleSelections: [] });
        })
        .finally(() => {
          set({
            fetchVehicleSelectionsCompleted: true,
            isFetchingVehicleSelections: false,
          });
        });
    },
    fetchVehicleTrims: (): Promise<void> => {
      const { year, make, model } = get();

      set({ fetchVehicleTrimsFailedMessage: null, trims: [] });

      const makeValue = lodashGet(make, 'value');
      const modelValue = lodashGet(model, 'value');
      if (!year || !makeValue || !modelValue) {
        return;
      }

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest
        .get<never, VehicleTrim[]>(
          `/vehicles/years/${year}/makes/${makeValue}/models/${modelValue}/trims`,
        )
        .then((trims) => {
          set({ trims });
        })
        .catch(() => {
          set({
            fetchVehicleTrimsFailedMessage: i18n.t(
              'errors.fetchVehicleTrims.defaultMessage',
            ),
          });
        });
    },
    findVehicleSelectionById: (
      id: string | undefined,
    ): DashboardVehicle | undefined => {
      if (!id) {
        return undefined;
      }

      const { vehicleSelections } = get();

      return find(vehicleSelections, (vehicle) => vehicle.id === id);
    },
    hydrate: async (params: HydrateParams): Promise<void> => {
      const {
        fetchVehicleIdentifiers,
        findVehicleSelectionById,
        payoffAmount: existingPayoffAmount,
      } = get();

      const { source } = params;

      let state: Partial<ApplyState>;

      if (source === HydrateSource.CANCELED_APPLICATION) {
        state = await toApplyStateFromCanceledApplication({
          fetchVehicleIdentifiers,
          params,
        });
      } else if (source === HydrateSource.MAIL_CODE) {
        state = toApplyStateFromMailCode(params);
      } else {
        state = await toApplyStateFromVehicleSelection({
          fetchVehicleIdentifiers,
          findVehicleSelectionById,
          params,
        });

        // We want to preserve the existing payoff amount if its available when hydrating
        // from a vehicle selection. This is done to prioritize the mail code payoff amount
        if (existingPayoffAmount) {
          state.payoffAmount = existingPayoffAmount;
        }
      }

      set(state);
    },
    loader(): Promise<Response | null> {
      const { applicationType, fetchVehicleSelections } = get();

      // pre-fetch vehicle selections if the application type is refinance
      if (applicationType === ApplicationType.REFINANCE) {
        fetchVehicleSelections();
      }

      return null;
    },
    resetSubmitValues(): void {
      set({
        isDuplicateVehicleError: false,
        submitCompleted: false,
        submitFailedMessage: null,
      });
    },
    setApplicationType: (value: ApplicationType): void => {
      const { applicationType } = get();

      const applicationTypeChanged = applicationType !== value;

      set({
        applicationType: value,

        ...(applicationTypeChanged && {
          payoffAmount: null,
          purchasePrice: null,
          purchaseType: null,
          refinanceGoal: null,
        }),
      });
    },
    setMakeByValue: (value: string): void => {
      const { makes } = get();

      const make = makes.find((item) => item.value === value) || null;

      const reset = !isEqual(make, get().make);

      set({
        ...(reset && {
          model: null,
          trim: null,
        }),
        make,
      });
    },
    setManualVehicleSelection(): void {
      set({
        make: null,
        mileage: null,
        model: null,
        payoffAmount: null,
        plate: null,
        searchVehicle: null,
        state: null,
        trim: null,
        vehicleSelectionId: null,
        vin: null,
        year: null,
      });
    },
    setMileage: (value: string): void => {
      set({ mileage: value });
    },
    setModelByValue: (value: string): void => {
      const { models } = get();

      const model = models.find((item) => item.value === value) || null;

      const reset = !isEqual(model, get().model);

      set({
        ...(reset && { trim: null }),
        model,
      });
    },
    setPayoffAmount: (value: string): void => {
      set({ payoffAmount: value });
    },
    setPurchasePrice: (value: string): void => {
      set({ purchasePrice: value });
    },
    setPurchaseType: (value: PurchaseType): void => {
      set({ purchaseType: value });
    },
    setRefinanceGoal: (value: RefinanceGoal): void => {
      set({ refinanceGoal: value });
    },
    setSearchVehicle: (value: SearchVehicle): void => {
      const { searchVehicle } = get();

      const searchVehicleChanged = searchVehicle !== value;

      set({
        searchVehicle: value,

        ...(searchVehicleChanged && {
          make: null,
          model: null,
          plate: null,
          state: null,
          trim: null,
          vin: null,
          year: null,
        }),
      });
    },
    setTrimByValue: (value: string): void => {
      const { trims } = get();

      const trim = trims.find((item) => item.value === value) || null;

      set({ trim });
    },
    setVehicleCondition: (value: VehicleCondition): void => {
      set({ vehicleCondition: value });
    },
    setVehicleSelectionId: (value: string): void => {
      set({ vehicleSelectionId: value });
    },
    setYear: (year: string): void => {
      const reset = !isEqual(year, get().year);

      set({
        ...(reset && {
          make: null,
          model: null,
          trim: null,
        }),
        year,
      });
    },
    submit(): Promise<void> {
      const {
        computed: { submitPayload },
        resetSubmitValues,
      } = get();

      const {
        sendVehicleDuplicateErrorAnalytics,
        sendVehicleDuplicateUpdateAnalytics,
        sendVehicleRegisteredAnalytics,
      } = useAnalyticStore.getState();

      const portalHttpRequest = useApiStore.getState().portalApi();

      resetSubmitValues();

      return portalHttpRequest
        .post<never, ApplyResponse>(`/2.0/apply`, submitPayload)
        .then(({ meta, vehicle }) => {
          const { addVehicle, setCurrentVehicle } =
            useDashboardStore.getState();

          if (meta.appliedAsDuplicate) {
            sendVehicleDuplicateUpdateAnalytics();
          } else {
            sendVehicleRegisteredAnalytics();
            // Only add new vehicles to the dashboard store.
            // Skip duplicates to avoid showing the same vehicle twice in the garage.
            addVehicle(vehicle);
          }

          // Update the current vehicle in the dashboard store
          // so that the user can be redirected to the applicant workflow
          setCurrentVehicle(vehicle);
        })
        .catch((error: unknown) => {
          const status = toString(lodashGet(error, 'response.status'));
          const isDuplicateVehicleError = status === '409';

          if (isDuplicateVehicleError) {
            sendVehicleDuplicateErrorAnalytics();
          }

          set({
            isDuplicateVehicleError,
            submitFailedMessage: lodashGet(
              i18n.t('errors.apply.statusMessage'),
              status,
              i18n.t('errors.apply.defaultMessage'),
            ),
          });
        })
        .finally(() => {
          set({ submitCompleted: true });
        });
    },
  });
